'use client'
import React, {
  useState,
  ReactNode,
  useRef,
  useCallback,
  useEffect,
  useMemo
} from 'react'
import { BannerCallout } from './banner-callout'
import { createRequiredContext, calloutVariants } from 'design-system'
import { VariantProps } from 'class-variance-authority'

const TIMEOUT = 3000

type BannerCalloutState = {
  open: boolean
  message: ReactNode
} & VariantProps<typeof calloutVariants>

const initialState: BannerCalloutState = {
  open: false,
  calloutType: 'info',
  message: ''
}

type BannerCalloutContextType = {
  state: BannerCalloutState
  setBannerCallout: (data: Partial<BannerCalloutState>) => void
  dismissBannerCallout: () => void
}

const [useBannerCallout, BannerCalloutContext] =
  createRequiredContext<BannerCalloutContextType>('BannerCallout')

export function BannerCalloutProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<BannerCalloutState>(initialState)

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const setBannerCallout = useCallback(
    (data: Partial<BannerCalloutState>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }

      setState({
        ...state,
        ...data,
        open: true
      })

      timeoutRef.current = setTimeout(() => {
        setState(prevState => ({ ...prevState, open: false }))
      }, TIMEOUT)
    },
    [state]
  )

  const dismissBannerCallout = useCallback(() => {
    setState(prevState => ({ ...prevState, open: false }))
  }, [])

  const contextValue = useMemo(
    () => ({
      state,
      setBannerCallout,
      dismissBannerCallout
    }),
    [state, setBannerCallout, dismissBannerCallout]
  )

  return (
    <BannerCalloutContext.Provider value={contextValue}>
      {children}
      <BannerCallout />
    </BannerCalloutContext.Provider>
  )
}

export { useBannerCallout }
